import { Connector } from '@knots/api-sdk'
import React from 'react'
import { Option } from '@/components/ui/Autocomplete/Autocomplete.types'
import { useField } from 'formik'
import { useSuspenseQuery } from '@tanstack/react-query'
import { ConnectorTemplateRequirement, useTemplateStepsContext } from '@/features/templates'
import { useZAFClient, isZendeskApp } from '@/features/zendesk-app'
import { connectorsQueries } from '@/features/connectors'
import { capitalCase } from 'change-case'

export const useSelectOptions = (system: string): Option[] => {
  const { data } = useSuspenseQuery(connectorsQueries.list())

  return React.useMemo(() => {
    const connectors = data?.filter((connector) => connector.system === system) ?? []

    return connectors.map((connector) => ({
      label: connector.name,
      value: connector.slug,
    }))
  }, [system, data])
}

export const useZendeskAppConnector = (): Connector => {
  const zafClient = useZAFClient()

  const { data } = useSuspenseQuery({
    queryKey: ['zendesk', 'connector'],
    queryFn: async () => {
      if (!zafClient) return null
      const context = await zafClient.context()
      const { subdomain } = context.account

      const data = {
        name: subdomain,
        subdomain,
      }

      return {
        system: 'ZENDESK',
        formData: JSON.stringify(data),
      }
    },
  })

  return data as Connector
}

export const useConnectorFormProps = ({ name, system }: { name: string; system: string }) => {
  const { currentStep, setCurrentStep } = useTemplateStepsContext()
  const [, , { setValue }] = useField(name)

  const zendeskConnector = useZendeskAppConnector()

  const [connector, setConnector] = React.useState<Connector>(
    isZendeskApp() && system === 'ZENDESK'
      ? zendeskConnector
      : ({
          system,
        } as Connector)
  )

  const onCreated = async (newConnector: Connector) => {
    setConnector(newConnector)
    setValue(newConnector.slug)
    setCurrentStep(currentStep + 1)
  }

  return {
    connector,
    onCreated,
  }
}

export const useConnectorRequirementDescription = (requirement: ConnectorTemplateRequirement) =>
  requirement.description ||
  `Set up a connector to link your Knots with ${capitalCase(requirement.system ?? 'system')}. This will enable seamless integration and data
  flow between route and your system.`
